import { createSignal, createMemo, onMount, For, Show } from "solid-js";
import { useSFetch } from "../hooks/sfetch";
import { useApi, isOk } from "../api";
import { useUserContext } from "../components/UserContextProvider";
import { useRoleParser } from "../hooks/permissions";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import { showConfirm } from "../components/Dialog";
import Text from "../components/Text";
import { EmptyState } from "../components/EmptyState";
import { SearchInput } from "../components/SearchInput";
import { DateRangePills } from "../components/DateRangePills";
import { Skeleton } from "../components/Skeleton";
import { formatDateTime } from "../services/dateFormat";
import type { FeatureFlagScope } from "../api";
import styles from "./FeatureFlagsAdmin.module.css";

type ScopeKind = "global" | "tenant" | "user";
type StatusFilter = "all" | "overridden" | "on" | "off";
const SCOPE_USER = 0;
const SCOPE_TENANT = 1;
const SCOPE_GLOBAL = 2;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

function scopeNumberFor(s: ScopeKind): FeatureFlagScope {
    if (s === "tenant") return SCOPE_TENANT as FeatureFlagScope;
    if (s === "user") return SCOPE_USER as FeatureFlagScope;
    return SCOPE_GLOBAL as FeatureFlagScope;
}

interface FeatureFlagDefinitionDto {
    key: string;
    defaultEnabled: boolean;
    description: string;
}

interface FeatureFlagOverrideDto {
    id: string;
    featureKey: string;
    scope: number;
    targetId: string;
    enabled: boolean;
    updatedAt: string;
}

interface TenantRow {
    id: string;
    name: string;
    slug: string;
}

interface UserRow {
    id: string;
    username: string;
}

export default function FeatureFlagsAdmin() {
    const sfetch = useSFetch();
    const api = useApi();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const { t } = useTranslation();
    const toast = useToast();

    const isPlatformAdmin = () => {
        const flags = parseRoles(user()?.permissions ?? 0, roles);
        return flags[roles.indexOf("Root")] ?? false;
    };

    const [scope, setScope] = createSignal<ScopeKind>("global");
    const [catalog, setCatalog] = createSignal<FeatureFlagDefinitionDto[]>([]);
    const [tenants, setTenants] = createSignal<TenantRow[]>([]);
    const [users, setUsers] = createSignal<UserRow[]>([]);
    const [selectedTenantId, setSelectedTenantId] = createSignal<string>("");
    const [selectedUserId, setSelectedUserId] = createSignal<string>("");
    const [overrides, setOverrides] = createSignal<FeatureFlagOverrideDto[]>([]);
    const [search, setSearch] = createSignal("");
    const [statusFilter, setStatusFilter] = createSignal<StatusFilter>("all");
    const [loading, setLoading] = createSignal(true);
    const [overridesLoading, setOverridesLoading] = createSignal(false);
    const [loadError, setLoadError] = createSignal(false);
    const [pendingKey, setPendingKey] = createSignal<string | null>(null);

    const targetId = createMemo(() => {
        if (scope() === "tenant") return selectedTenantId();
        if (scope() === "user") return selectedUserId();
        return EMPTY_GUID;
    });

    const overrideMap = createMemo(() => {
        const map = new Map<string, FeatureFlagOverrideDto>();
        for (const o of overrides()) map.set(o.featureKey, o);
        return map;
    });

    const overriddenCount = createMemo(() => overrideMap().size);

    const filteredCatalog = createMemo(() => {
        const q = search().trim().toLowerCase();
        const status = statusFilter();
        const ovrMap = overrideMap();
        return catalog().filter((def) => {
            if (q && !def.key.toLowerCase().includes(q) && !def.description.toLowerCase().includes(q)) return false;
            const ovr = ovrMap.get(def.key);
            const effective = ovr ? ovr.enabled : def.defaultEnabled;
            if (status === "overridden" && !ovr) return false;
            if (status === "on" && !effective) return false;
            if (status === "off" && effective) return false;
            return true;
        });
    });

    async function loadCatalog() {
        const res = await api.featureFlagsAdmin.getCatalog();
        if (isOk(res.data)) {
            setCatalog(res.data.value.entries as FeatureFlagDefinitionDto[]);
            setLoadError(false);
        } else {
            setLoadError(true);
            toast?.error(t("FEATURE_FLAGS_LOAD_ERROR"));
        }
    }

    async function loadTenants() {
        const res = await api.tenantAdmin.getAllTenants();
        if (isOk(res.data)) setTenants((res.data.value.tenants as any[]).map(x => ({ id: x.id, name: x.name, slug: x.slug })));
    }

    async function loadUsers() {
        const res = await sfetch("/api/User/Users");
        if (res.ok) setUsers(((res.value ?? []) as any[]).map(x => ({ id: x.id, username: x.username })));
    }

    async function loadOverrides() {
        const tid = targetId();
        if (!tid) {
            setOverrides([]);
            return;
        }
        setOverridesLoading(true);
        const scopeNum = scopeNumberFor(scope());
        const res = await api.featureFlagsAdmin.listOverrides({ scope: scopeNum, targetId: tid });
        if (isOk(res.data)) setOverrides(res.data.value.overrides as FeatureFlagOverrideDto[]);
        setOverridesLoading(false);
    }

    onMount(async () => {
        await Promise.all([loadCatalog(), loadTenants(), loadUsers()]);
        await loadOverrides();
        setLoading(false);
    });

    async function setOverride(key: string, enabled: boolean) {
        const tid = targetId();
        if (!tid) return;
        setPendingKey(key);
        const scopeNum = scopeNumberFor(scope());
        const res = await api.featureFlagsAdmin.setOverride({ key, scope: scopeNum, targetId: tid, enabled });
        if (isOk(res.data)) {
            toast?.info(t("FEATURE_FLAGS_SAVED"));
            await loadOverrides();
        } else {
            toast?.error(t("FEATURE_FLAGS_SAVE_ERROR"));
        }
        setPendingKey(null);
    }

    async function clearOverride(key: string) {
        const tid = targetId();
        if (!tid) return;
        const ok = await showConfirm(t("FEATURE_FLAGS_REVERT_CONFIRM"));
        if (!ok) return;
        setPendingKey(key);
        const scopeNum = scopeNumberFor(scope());
        const res = await api.featureFlagsAdmin.removeOverride({ key, scope: scopeNum, targetId: tid });
        if (isOk(res.data)) {
            toast?.info(t("FEATURE_FLAGS_REMOVED"));
            await loadOverrides();
        } else {
            toast?.error(t("FEATURE_FLAGS_SAVE_ERROR"));
        }
        setPendingKey(null);
    }

    function onScopeChange(next: ScopeKind) {
        setScope(next);
        setOverrides([]);
        if (next === "global") {
            setSelectedTenantId("");
            setSelectedUserId("");
            loadOverrides();
        }
    }

    function onTenantChange(id: string) {
        setSelectedTenantId(id);
        loadOverrides();
    }

    function onUserChange(id: string) {
        setSelectedUserId(id);
        loadOverrides();
    }

    return (
        <Show when={isPlatformAdmin()} fallback={
            <div class={styles.forbidden}>
                <h1><Text val="FEATURE_FLAGS_FORBIDDEN_TITLE" /></h1>
                <p><Text val="FEATURE_FLAGS_FORBIDDEN_BODY" /></p>
            </div>
        }>
            <div class={styles.container}>
                <div class={styles.headerRow}>
                    <h1><Text val="FEATURE_FLAGS_TITLE" /></h1>
                    <Show when={!loading()}>
                        <span class={styles.entryCount}>
                            {t("FEATURE_FLAGS_SUMMARY", filteredCatalog().length, catalog().length, overriddenCount())}
                        </span>
                    </Show>
                </div>

                <div class={styles.toolbar}>
                    <SearchInput
                        value={search()}
                        onInput={setSearch}
                        placeholder={t("FEATURE_FLAGS_SEARCH_PLACEHOLDER")}
                        ariaLabel={t("FEATURE_FLAGS_SEARCH_PLACEHOLDER")}
                    />
                    <DateRangePills
                        value={statusFilter()}
                        onChange={setStatusFilter}
                        pills={[
                            { value: "all", labelKey: "FEATURE_FLAGS_FILTER_ALL" },
                            { value: "overridden", labelKey: "FEATURE_FLAGS_FILTER_OVERRIDDEN" },
                            { value: "on", labelKey: "FEATURE_FLAGS_FILTER_ON" },
                            { value: "off", labelKey: "FEATURE_FLAGS_FILTER_OFF" },
                        ]}
                    />
                </div>

                <div class={styles.controls}>
                    <div class={styles.controlField}>
                        <span class={styles.controlLabel}><Text val="FEATURE_FLAGS_SCOPE_LABEL" /></span>
                        <select value={scope()} onChange={(e) => onScopeChange(e.currentTarget.value as ScopeKind)}>
                            <option value="global">{t("FEATURE_FLAGS_SCOPE_GLOBAL")}</option>
                            <option value="tenant">{t("FEATURE_FLAGS_SCOPE_TENANT")}</option>
                            <option value="user">{t("FEATURE_FLAGS_SCOPE_USER")}</option>
                        </select>
                    </div>

                    <Show when={scope() === "tenant"}>
                        <div class={styles.controlField}>
                            <span class={styles.controlLabel}><Text val="FEATURE_FLAGS_TARGET_TENANT" /></span>
                            <select value={selectedTenantId()} onChange={(e) => onTenantChange(e.currentTarget.value)}>
                                <option value="">{t("FEATURE_FLAGS_PICK_TARGET")}</option>
                                <For each={tenants()}>{(tn) => <option value={tn.id}>{tn.name}</option>}</For>
                            </select>
                        </div>
                    </Show>

                    <Show when={scope() === "user"}>
                        <div class={styles.controlField}>
                            <span class={styles.controlLabel}><Text val="FEATURE_FLAGS_TARGET_USER" /></span>
                            <select value={selectedUserId()} onChange={(e) => onUserChange(e.currentTarget.value)}>
                                <option value="">{t("FEATURE_FLAGS_PICK_TARGET")}</option>
                                <For each={users()}>{(u) => <option value={u.id}>{u.username}</option>}</For>
                            </select>
                        </div>
                    </Show>

                    <Show when={overridesLoading()}>
                        <i class={`fa-solid fa-spinner fa-spin ${styles.inlineSpinner}`} aria-hidden="true" />
                    </Show>
                </div>

                <Show when={loading()}>
                    <div class={styles.list}>
                        <Skeleton variant="rect" height="6.5rem" radius="var(--radius-lg)" count={6} />
                    </div>
                </Show>

                <Show when={!loading() && loadError() && catalog().length === 0}>
                    <EmptyState icon="fa-triangle-exclamation" title={t("FEATURE_FLAGS_LOAD_ERROR")} />
                </Show>

                <Show when={!loading() && !loadError() && catalog().length > 0 && filteredCatalog().length === 0}>
                    <EmptyState icon="fa-magnifying-glass" title={t("FEATURE_FLAGS_NO_RESULTS_TITLE")} hint={t("FEATURE_FLAGS_NO_RESULTS_HINT")} />
                </Show>

                <Show when={!loading() && filteredCatalog().length > 0}>
                    <div class={styles.list}>
                        <For each={filteredCatalog()}>{(def) => {
                            const ovr = () => overrideMap().get(def.key);
                            const hasOverride = () => ovr() !== undefined;
                            const effective = () => hasOverride() ? (ovr()!.enabled) : def.defaultEnabled;
                            const editable = () => scope() === "global" || (targetId() !== "" && targetId() !== EMPTY_GUID);
                            const isPending = () => pendingKey() === def.key;
                            return (
                                <div class={`fcard ${styles.row}`}>
                                    <div class={styles.rowHeader}>
                                        <div>
                                            <div class={styles.flagKey}>{def.key}</div>
                                            <div class={styles.flagDefault}>
                                                {def.defaultEnabled ? t("FEATURE_FLAGS_DEFAULT_ON") : t("FEATURE_FLAGS_DEFAULT_OFF")}
                                            </div>
                                        </div>
                                        <span class={`${styles.effectiveBadge} ${effective() ? styles.effectiveOn : styles.effectiveOff}`}>
                                            {effective() ? t("FEATURE_FLAGS_ON") : t("FEATURE_FLAGS_OFF")}
                                        </span>
                                    </div>
                                    <div class={styles.flagDescription}>{def.description}</div>
                                    <Show when={hasOverride()}>
                                        <div class={styles.overrideMeta}>
                                            <i class="fa-solid fa-pen-to-square" aria-hidden="true" />
                                            {t("FEATURE_FLAGS_LAST_CHANGED", formatDateTime(ovr()!.updatedAt))}
                                        </div>
                                    </Show>
                                    <Show when={editable()}>
                                        <div class={styles.toggleGroup}>
                                            <button
                                                classList={{ [styles.active]: !hasOverride() }}
                                                onClick={() => clearOverride(def.key)}
                                                disabled={!hasOverride() || isPending()}
                                            >{t("FEATURE_FLAGS_INHERIT")}</button>
                                            <button
                                                classList={{ [styles.active]: hasOverride() && ovr()!.enabled === true }}
                                                onClick={() => setOverride(def.key, true)}
                                                disabled={isPending()}
                                            >{t("FEATURE_FLAGS_ON")}</button>
                                            <button
                                                classList={{ [styles.active]: hasOverride() && ovr()!.enabled === false }}
                                                onClick={() => setOverride(def.key, false)}
                                                disabled={isPending()}
                                            >{t("FEATURE_FLAGS_OFF")}</button>
                                        </div>
                                    </Show>
                                    <Show when={!editable()}>
                                        <div class={styles.editHint}><Text val="FEATURE_FLAGS_PICK_TARGET" /></div>
                                    </Show>
                                </div>
                            );
                        }}</For>
                    </div>
                </Show>
            </div>
        </Show>
    );
}
